import { Formik } from 'formik';
import { useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';
import { IServiceAdvisor, useServiceAdvisors } from '../../API/GoMoto';
import { useAppointmentState, useAppointmentUpdateAndPersist, useIsFullMobileCheckInAllowed } from '../../context/Appointment/appointment';
import { useDefaultServiceAdvisor, useKioskState } from '../../context/Kiosk/kiosk';
import { CTAButton } from '../../controls/ButtonBase';
import { useLocalization, useLocalizationExtension } from '../../context/Localization/localization';
import { Heading } from '../../controls/Heading/Heading';
import { KeyboardLayouts } from '../../controls/SimpleKeyboard/Layout';
import { Spinner } from '../../controls/Spinner/Spinner';
import { GMFormAutocompleteSelect } from '../../controls/Autocomplete';
import { isElectron } from '../../helpers/EnvironmentFlags/EnvironmentFlags';
import { textCapitalize } from '../../helpers/textCapitalize';
import { nameof } from '../../helpers/nameof';
import { GMFormTagNumberField } from '../../controls/TextField';
import { GMAppointmentName } from '../../controls/AppointmentName';
import { GMFormSelect, GMMenuItem } from '../../controls/Select';
import { GMForm, GMFormWrapper } from '../../controls/Form';
import { GoMotoComponent } from '../../API/GoMoto.types';

interface IProps {
    onProceed: () => void;
    onSkip: () => void;
}

export const TagNumber: GoMotoComponent<IProps> = ({ onProceed, onSkip }) => {
    const Localization = useLocalization();
    const { formatAsString, formatInvalid } = useLocalizationExtension();
    const appointment = useAppointmentState();
    const { tagNumber, serviceAdvisorNumber } = appointment;
    const initialValues = {
        tagNumber: tagNumber || '',
        serviceAdvisorNumber: serviceAdvisorNumber || '',
    };
    const setAppointment = useAppointmentUpdateAndPersist();
    const {
        key: kioskKey,
        dealer: { serviceAdvisorEnabled, tagNumberEnabled, tagNumberRequired, key: dealerKey },
    } = useKioskState();
    const defaultServiceAdvisor = useDefaultServiceAdvisor();
    const getServiceAdvisors = useServiceAdvisors();
    const [advisors, setAdvisors] = useState<IServiceAdvisor[] | undefined>(undefined);
    const [retrievedAdvisors, setRetrievedAdvisors] = useState<boolean>(false);
    const isFullMobileCheckInAllowed = useIsFullMobileCheckInAllowed();
    const showTagNumber = isElectron() || isFullMobileCheckInAllowed;
    const skipping = useRef<boolean>(false);

    useEffect(() => {
        (async () => {
            if (!skipping.current && !serviceAdvisorEnabled && (!showTagNumber || !tagNumberEnabled)) {
                skipping.current = true;
                if (!appointment.serviceAdvisorNumber && defaultServiceAdvisor) {
                    await setAppointment({
                        ...appointment,
                        serviceAdvisorNumber: defaultServiceAdvisor.id,
                        serviceAdvisorName: defaultServiceAdvisor.name,
                    });
                }
                onSkip();
            }
            else if (retrievedAdvisors !== true) {
                const data = await getServiceAdvisors(kioskKey, dealerKey, appointment.serviceAdvisorNumber, appointment.serviceAdvisorName, true);
                setAdvisors(data);
                setRetrievedAdvisors(true);
            }
        })();
    }, [
        onSkip,
        serviceAdvisorEnabled,
        tagNumberEnabled,
        showTagNumber,
        appointment,
        defaultServiceAdvisor,
        setAppointment,
        getServiceAdvisors,
        kioskKey,
        dealerKey,
        retrievedAdvisors,
    ]);

    useEffect(() => {
        if (serviceAdvisorEnabled && advisors?.length === 1 && !appointment.serviceAdvisorNumber) {
            appointment.serviceAdvisorNumber = advisors[0].id;
            appointment.serviceAdvisorName = advisors[0].name;
        }
    }, [advisors, appointment, serviceAdvisorEnabled]);

    const validation = {
        [nameof<typeof initialValues>('tagNumber')]: Yup.string()
            .min(1, formatAsString(Localization.TagNumberShouldBeBetween))
            .max(4, formatAsString(Localization.TagNumberShouldBeBetween)),
        [nameof<typeof initialValues>('serviceAdvisorNumber')]: Yup.string()
            .nullable(),
    };
    if (serviceAdvisorEnabled) {
        if (advisors) {
            validation[nameof<typeof initialValues>('serviceAdvisorNumber')] = Yup.string()
                .nullable()
                .oneOf(
                    advisors.map((advisor) => advisor.id),
                    formatInvalid(Localization.ServiceAdvisor),
                );
        }
    }

    const secondaryHeading =
        showTagNumber && tagNumberEnabled && serviceAdvisorEnabled
            ? Localization.EnterTagNumberAndSelectAdvisorOptional
            : showTagNumber && tagNumberEnabled
                ? Localization.EnterTagNumber
                : Localization.SelectAdvisorOptional;

    return ((!serviceAdvisorEnabled && (!showTagNumber || !tagNumberEnabled)) || !retrievedAdvisors) ? (
        <Spinner />
    ) : (
        <Formik
            initialValues={initialValues}
            onSubmit={async (values) => {
                let serviceAdvisorName = appointment.serviceAdvisorName;
                let serviceAdvisorNumber = appointment.serviceAdvisorNumber;

                if (serviceAdvisorEnabled) {
                    serviceAdvisorNumber = values.serviceAdvisorNumber;
                    serviceAdvisorName = advisors?.find((advisor) => advisor.id === values.serviceAdvisorNumber)?.name;
                }
                else if (!appointment.serviceAdvisorNumber && defaultServiceAdvisor) {
                    serviceAdvisorNumber = defaultServiceAdvisor.id;
                    serviceAdvisorName = defaultServiceAdvisor.name;
                }

                values.tagNumber = values.tagNumber.toUpperCase();

                return setAppointment({
                    ...appointment,
                    ...values,
                    serviceAdvisorNumber,
                    serviceAdvisorName,
                })
                    .then(onProceed);
            }}
            validationSchema={Yup.object()
                .shape(validation)}
        >
            {({ values }) => (
                <GMForm
                    heading={
                        <Heading
                            primaryHeading={
                                <GMAppointmentName />
                            }
                            secondaryHeading={secondaryHeading}
                            leftAlign
                        />
                    }
                    submitButton={(
                        <CTAButton
                            styleType={'primary'}
                            type={'submit'}
                            title={Localization.ClickToProceedToNextStep}
                            label={Localization.Proceed}
                            id={Localization.ClickToProceedToNextStep}
                            ariaLabel={Localization.ClickToProceedToNextStep}
                        />
                    )}
                    topDivider
                    hideKeyboard={!(showTagNumber && tagNumberEnabled)}
                >
                    {showTagNumber && tagNumberEnabled && (
                        <>
                            {tagNumberRequired && <GMFormWrapper finePrint requiredFieldLabel>{Localization.RequiredFields}</GMFormWrapper>}
                            <GMFormTagNumberField
                                name={nameof<typeof values>('tagNumber')}
                                id={nameof<typeof values>('tagNumber')}
                                title={Localization.KeyTagNumber}
                                label={Localization.KeyTagNumber}
                                ariaLabel={Localization.KeyTagNumber}
                                required={tagNumberRequired}
                                keyboardLayout={KeyboardLayouts.AlphanumericNumpad}
                                capsLock
                                autoFocus
                            />
                        </>
                    )}
                    {serviceAdvisorEnabled && isElectron() && (
                        <>
                            <GMFormSelect
                                id={Localization.ServiceAdvisor}
                                name={nameof<typeof values>('serviceAdvisorNumber')}
                                label={Localization.ServiceAdvisor}
                                customPlaceholder={<>&nbsp;</>}
                            >
                                {advisors?.map((advisor) => (
                                    <GMMenuItem key={advisor.id} value={advisor.id}>
                                        {textCapitalize(advisor.name)}
                                    </GMMenuItem>
                                )) || <GMMenuItem key={values.serviceAdvisorNumber} value={values.serviceAdvisorNumber} />}
                            </GMFormSelect>
                        </>
                    )}
                    {serviceAdvisorEnabled && !isElectron() && (
                        <GMFormAutocompleteSelect
                            name={nameof<typeof values>('serviceAdvisorNumber')}
                            id={nameof<typeof values>('serviceAdvisorNumber')}
                            title={Localization.ServiceAdvisor}
                            label={Localization.ServiceAdvisor}
                            ariaLabel={Localization.ServiceAdvisor}
                            fullOptions={advisors ?? []}
                            forceBottomPopper={true}
                        />
                    )}
                </GMForm>
            )}
        </Formik>
    );
};
